import React, { useEffect, useState, useContext } from "react";
import H5 from "components/typography/H5";
import { Box, Divider, Flex, Grid, Label, Link, Spinner, Badge, Button } from "theme-ui";
import { themevals } from "theme/themevals";
import FileUpload from "./FileUpload";
import moment from "moment-timezone";
import { createColumnHelper } from "@tanstack/react-table";
import TableBasic from "components/ui/Table";
import { TrashIcon, QuestionMarkIcon, EllipsisIcon } from "components/svg";
import BasicModal from "components/ui/BasicModal";
import BasicButton from "components/forms/BasicButton";
import P from "components/typography/P";
import { useToast } from "components/toast";
import UserContext from "context/User";
import { trackActivity } from "api/ActivityTracker";
import { getPOFiles } from "api/PurchaseOrder";
import { deletePOFile } from "api/PurchaseOrder";

const Files = ({ isCompany, po, reloadPO }: { isCompany: boolean; po: any; reloadPO: (po: any) => void }) => {
  const [files, setFiles] = useState<any[] | null>(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const columnHelper = createColumnHelper();
  const toast = useToast();
  const { user } = useContext<any>(UserContext);

  const _init = async () => {
    const res = await getPOFiles({ data: po });
    if (res?.length) {
      setFiles(res);
    } else {
      setFiles([]);
    }
  };

  useEffect(() => {
    _init();
  }, []);

  const _confirmFileDelete = async (file: any) => {
    setConfirmModalOpen(true);
    setSelectedFile(file);
  };

  const columns = [
    columnHelper.accessor((row: any) => row?.name, {
      id: "id",
      cell: (info: any) => {
        const row = info.row.original;
        return (
          <Box sx={{ color: themevals.colors.black, cursor: "pointer" }} onClick={() => window.open(row?.url, "_blank")}>
            {info.getValue()}
          </Box>
        );
      },
      header: () => <span>File</span>,
    }),
    columnHelper.accessor((row: any) => row?.createdAt, {
      id: "dateAdded",
      cell: (info: any) => (info.getValue() ? moment(info.getValue()).format("MMM D, YYYY") : ""),
      header: () => <span>Date Added</span>,
    }),
    columnHelper.accessor((row: any) => row?.uploadedBy, {
      id: "uploadedBy",
      cell: (info: any) => `${info.getValue()?.firstName} ${info.getValue()?.lastName}`,
      header: () => <span>Uploaded By</span>,
    }),
    columnHelper.accessor("actions", {
      id: "actions",
      cell: (info: any) => {
        const row = info.row.original;
        if (row?.uploadedBy?.id !== user?.id) return null;
        return (
          <Box onClick={() => _confirmFileDelete(row)} sx={{ cursor: "pointer" }}>
            <TrashIcon fill={themevals.colors.dkgrey} width="14px" height="14px" />
          </Box>
        );
      },
      header: () => null,
      enableSorting: false,
      meta: {
        style: { width: "10px" },
      },
    }),
  ];

  const _cancelConfirmModal = () => {
    setConfirmModalOpen(false);
    setSelectedFile(null);
  };

  const _deleteFile = async (file: any) => {
    setSubmitting(true);
    const res = await deletePOFile({ data: { id: po.id, fileId: file.id } });
    if (res?.success) {
      setSubmitting(false);
      toast.add(`File deleted.`, "var(--theme-ui-colors-success)");
      void trackActivity({
        module: "purchase_orders",
        action: "delete_purchase_order_file",
        actionType: "delete",
        userId: user?.id || null,
        companyId: user?.company?.id || null,
        entityType: "purchase_order_file",
        entityId: file.id,
        metadata: {
          fileId: file.id,
          fileName: file.name || null,
        },
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      setConfirmModalOpen(false);
      setSelectedFile(null);
      _init();
    } else {
      setSubmitting(false);
      toast.add(`Error deleting file.`, "var(--theme-ui-colors-danger)");
      setConfirmModalOpen(false);
      setSelectedFile(null);
    }
  };

  return (
    <>
      <H5 fontFamily="Rubik" fontSize="16px" marginLeft="0px" marginTop="0px">
        Files
      </H5>
      <Flex sx={{ justifyContent: "flex-end" }}>
        <FileUpload title={"Add File"} po={po} afterUpload={_init} project={po.project} />
      </Flex>
      <Flex sx={{ width: "100%", marginTop: "20px", flexDirection: "column" }}>
        {files === null ? (
          <Flex sx={{ justifyContent: "center", alignItems: "center", width: "100%" }}>
            <Spinner size={24} />
          </Flex>
        ) : files.length > 0 ? (
          <TableBasic
            data={files}
            columns={columns}
            enablePaging={false}
            enableSearch={false}
            customStyles={{ border: `1px solid ${themevals.colors.inputOutline}` }}
            noFirstColumnMinWidth={true}
            size="sm"
          />
        ) : (
          <Flex sx={{ justifyContent: "flex-start", alignItems: "center", width: "100%" }}>
            <P fontFamily="Poppins" fontSize="14px" color="gray">
              No files yet.
            </P>
          </Flex>
        )}
      </Flex>
      {confirmModalOpen && (
        <BasicModal
          title={`Delete File?`}
          cancel_click={() => _cancelConfirmModal()}
          cancel_text="No, cancel"
          $submitting={submitting}
          size={"sm"}
          icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
          actions={
            <BasicButton
              type="button"
              scheme="danger"
              onClick={() => _deleteFile(selectedFile)}
              $submitting={submitting}
              $iconHeight="14px"
              $iconWidth="14px"
              $loadingColor="#fff"
              keepText={true}
              styles={{ margin: "0 0.625rem .25rem 0" }}
            >
              {!submitting && <TrashIcon fill={"#fff"} />}
              {"Delete File"}
            </BasicButton>
          }
        >
          <P lineHeight={"22px"}>Are you sure you want to delete the file from the purchase order?</P>
        </BasicModal>
      )}
    </>
  );
};

export default Files;
